import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchHosts, Host, HostsQueryParams } from "@/lib/api/hosts";
import { 
  ChevronLeft, 
  ChevronRight, 
  Search, 
  MapPin, 
  Calendar, 
  Mail,
  Phone,
  Star,
  Target,
  Award,
  Info
} from "lucide-react";

export default function HostsPage({ session, profile }: { session: any; profile: any }) {
  const [queryParams, setQueryParams] = useState<HostsQueryParams>({
    page: 1,
    limit: 10,
  });
  const [searchText, setSearchText] = useState("");

  // Fetch hosts data with pagination and filters
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["hosts", queryParams],
    queryFn: () => fetchHosts(queryParams, session),
    keepPreviousData: true,
  });

  const handleSearch = () => {
    setQueryParams({ ...queryParams, searchText, page: 1 });
  };

  const handleFilterChange = (key: keyof HostsQueryParams, value: any) => {
    setQueryParams({ ...queryParams, [key]: value, page: 1 });
  };

  const handlePageChange = (newPage: number) => {
    setQueryParams({ ...queryParams, page: newPage });
  };

  // Parse languages from string to array
  const parseLanguages = (languagesStr?: string): string[] => {
    if (!languagesStr) return [];
    return languagesStr.split(',').map(lang => lang.trim());
  };

  // Parse focus areas from string to array
  const parseFocusAreas = (focusAreaStr?: string): string[] => {
    if (!focusAreaStr) return [];
    return focusAreaStr.split(',').map(area => area.trim());
  };

  // Parse top SKUs from string to array
  const parseTopSkus = (topSkusStr?: string): string[] => {
    if (!topSkusStr) return [];
    return topSkusStr.split(',').map(sku => sku.trim());
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-1/4" />
        <div className="border rounded-lg">
          <div className="space-y-4 p-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Hosts</h1>
        <div className="border rounded-lg p-6 text-center">
          <p className="text-red-500">Error loading hosts: {(error as Error).message}</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => setQueryParams({ ...queryParams })}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const hosts = data?.data || [];
  const pagination = data?.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Hosts</h1>
        <div className="text-sm text-muted-foreground">
          Total: {pagination.total} hosts
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="relative">
          <Input
            placeholder="Search by name, city or state"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="pl-10 pr-16"
          />
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Button 
            variant="ghost" 
            size="sm" 
            className="absolute right-1 top-1 px-2"
            onClick={handleSearch}
          >
            Search
          </Button>
        </div>

        <Select
          value={queryParams.city || "all_cities"}
          onValueChange={(value) => handleFilterChange("city", value === "all_cities" ? undefined : value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Filter by city" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all_cities">All Cities</SelectItem>
            <SelectItem value="New Delhi">New Delhi</SelectItem>
            <SelectItem value="Bangalore">Bangalore</SelectItem>
            <SelectItem value="Mumbai">Mumbai</SelectItem>
            <SelectItem value="Chennai">Chennai</SelectItem>
            <SelectItem value="Pune">Pune</SelectItem>
            <SelectItem value="Rajkot">Rajkot</SelectItem>
            <SelectItem value="Thane">Thane</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={queryParams.state || "all_states"}
          onValueChange={(value) => handleFilterChange("state", value === "all_states" ? undefined : value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Filter by state" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all_states">All States</SelectItem>
            <SelectItem value="Delhi">Delhi</SelectItem>
            <SelectItem value="Karnataka">Karnataka</SelectItem>
            <SelectItem value="Maharashtra">Maharashtra</SelectItem>
            <SelectItem value="Tamil Nadu">Tamil Nadu</SelectItem>
            <SelectItem value="Gujarat">Gujarat</SelectItem>
            <SelectItem value="Uttar Pradesh">Uttar Pradesh</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={queryParams.gender || "all_genders"}
          onValueChange={(value) => handleFilterChange("gender", value === "all_genders" ? undefined : value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Filter by gender" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all_genders">All Genders</SelectItem>
            <SelectItem value="Male">Male</SelectItem>
            <SelectItem value="Female">Female</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Hosts Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Host</TableHead>
              <TableHead>About</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Experience</TableHead>
              <TableHead>Focus Areas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {hosts.map((host: Host) => (
              <TableRow key={host.id} className="cursor-pointer hover:bg-muted/50">
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarFallback>
                        {host.name.split(" ").map(n => n[0]).join("")}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium">{host.name}</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Since {new Date(host.created_at).toLocaleDateString()}
                      </div>
                      {host.meta.pronouns && (
                        <Badge variant="outline" className="mt-1 text-xs">
                          {host.meta.pronouns}
                        </Badge>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="max-w-[240px]">
                  <div className="flex flex-col gap-2">
                    {host.meta.about && (
                      <div 
                        className="text-sm line-clamp-2 mb-2 relative group cursor-help"
                        title={host.meta.about}
                      >
                        {host.meta.about}
                        <div className="absolute z-50 left-0 top-full mt-1 p-2 bg-white border rounded-md shadow-lg w-56 hidden group-hover:block text-sm">
                          {host.meta.about}
                        </div>
                      </div>
                    )}
                    {host.meta.image ? (
                      <img src={host.meta.image} alt={host.name} className="w-full h-36 object-cover rounded-lg" />
                    ) : (
                      <span></span>)}
                    {host.meta.lanugages && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        <span className="text-xs text-muted-foreground mr-1">Languages:</span>
                        {parseLanguages(host.meta.lanugages).map((lang, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {lang}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell className="max-w-[240px]">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {host.meta.city || "N/A"}, {host.meta.state || "N/A"}
                    </span>
                  </div>
                  {host.meta.zip && (
                    <div className="text-sm text-muted-foreground ml-6">
                      ZIP: {host.meta.zip}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-500" />
                    <span>{host.meta.experience || "0"} years</span>
                  </div>
                  {host.meta.rate_card && (
                    <div className="text-sm text-muted-foreground ml-6">
                      Rate: ₹{host.meta.rate_card}/day
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-2">
                    {host.meta.focus_area && (
                      <div className="flex flex-wrap gap-1">
                        <Target className="h-4 w-4 text-muted-foreground mr-1 shrink-0 mt-0.5" />
                        <div className="flex flex-wrap gap-1">
                          {parseFocusAreas(host.meta.focus_area).slice(0, 2).map((area, idx) => (
                            <Badge key={idx} variant="outline" className="bg-primary/10">
                              {area}
                            </Badge>
                          ))}
                          {parseFocusAreas(host.meta.focus_area).length > 2 && (
                            <Badge variant="outline">
                              +{parseFocusAreas(host.meta.focus_area).length - 2} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                    {host.meta.top_skus_hosted && (
                      <div className="flex flex-wrap gap-1">
                        <Award className="h-4 w-4 text-muted-foreground mr-1 shrink-0 mt-0.5" />
                        <div className="flex flex-wrap gap-1">
                          {parseTopSkus(host.meta.top_skus_hosted).slice(0, 1).map((sku, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {sku}
                            </Badge>
                          ))}
                          {parseTopSkus(host.meta.top_skus_hosted).length > 1 && (
                            <Badge variant="outline" className="text-xs">
                              +{parseTopSkus(host.meta.top_skus_hosted).length - 1} more events
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {hosts.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                  No hosts found matching your criteria
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total} hosts
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="sr-only">Previous Page</span>
            </Button>
            <div className="text-sm">
              Page {pagination.page} of {pagination.totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
            >
              <ChevronRight className="h-4 w-4" />
              <span className="sr-only">Next Page</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
